
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEnhancedAuth } from '@/hooks/useEnhancedAuth';
import { toast } from 'sonner';

export const useSecureAdminActions = () => {
  const { adminUser, isSuperAdmin, logSecurityEvent } = useEnhancedAuth();
  const queryClient = useQueryClient();

  // Secure admin user update with role validation
  const updateAdminUserMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      await logSecurityEvent('ADMIN_USER_UPDATE_ATTEMPT', { targetUserId: id, updates });

      // Validate role changes
      if (updates.role && !isSuperAdmin) {
        throw new Error('Only super admins can change user roles');
      }

      // Prevent self-privilege reduction
      if (id === adminUser?.id) {
        if ((updates.role && updates.role !== adminUser.role) || 
            (updates.is_active === false)) {
          throw new Error('Cannot reduce your own privileges');
        }
      }

      const { error } = await supabase
        .from('admin_users')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      await logSecurityEvent('ADMIN_USER_UPDATE_SUCCESS', { targetUserId: id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Admin user updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update admin user');
      logSecurityEvent('ADMIN_USER_UPDATE_FAILED', { error: error.message });
    },
  });

  // Secure password reset with validation
  const requestPasswordResetMutation = useMutation({
    mutationFn: async ({ email, targetUserId }: { email: string; targetUserId?: string }) => {
      await logSecurityEvent('PASSWORD_RESET_REQUEST', { targetEmail: email, targetUserId });

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/admin/reset-password`
      });

      if (error) throw error;

      await logSecurityEvent('PASSWORD_RESET_EMAIL_SENT', { targetEmail: email });
    },
    onSuccess: () => {
      toast.success('Password reset email sent successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to send password reset email');
      logSecurityEvent('PASSWORD_RESET_FAILED', { error: error.message });
    },
  });

  // Create new admin user (super admin only)
  const createAdminUserMutation = useMutation({
    mutationFn: async (adminData: { email: string; username: string; role: string }) => {
      if (!isSuperAdmin) {
        throw new Error('Only super admins can create new admin users');
      }

      await logSecurityEvent('ADMIN_USER_CREATE_ATTEMPT', { targetEmail: adminData.email });

      const { error } = await supabase
        .from('admin_users')
        .insert({
          email: adminData.email,
          username: adminData.username,
          role: adminData.role,
          is_active: true,
          user_id: null // Will be updated when user signs up
        });

      if (error) throw error;

      await logSecurityEvent('ADMIN_USER_CREATE_SUCCESS', { targetEmail: adminData.email });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Admin user created successfully. They need to sign up with the specified email.');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create admin user');
      logSecurityEvent('ADMIN_USER_CREATE_FAILED', { error: error.message });
    },
  });

  // Delete admin user (super admin only, with safeguards)
  const deleteAdminUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!isSuperAdmin) {
        throw new Error('Only super admins can delete admin users');
      }

      if (userId === adminUser?.id) {
        throw new Error('Cannot delete your own account');
      }

      await logSecurityEvent('ADMIN_USER_DELETE_ATTEMPT', { targetUserId: userId });

      const { error } = await supabase
        .from('admin_users')
        .delete()
        .eq('id', userId);

      if (error) throw error;

      await logSecurityEvent('ADMIN_USER_DELETE_SUCCESS', { targetUserId: userId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Admin user deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete admin user');
      logSecurityEvent('ADMIN_USER_DELETE_FAILED', { error: error.message });
    },
  });

  return {
    updateAdminUserMutation,
    requestPasswordResetMutation,
    createAdminUserMutation,
    deleteAdminUserMutation,
    isSuperAdmin,
    canManageUsers: isSuperAdmin,
    canViewUsers: Boolean(adminUser),
  };
};
